import React, { useEffect, useMemo, useState } from "react";

import {
  Button,
  Switch,
  TextField,
  Autocomplete,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";

import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import {
  formatByText,
  onChangeMask,
  formatProductNameAndDescription,
} from "utils";
import { CoverageAreaProps } from "store/modules/Catalog/types.store";
import { ECoverageAreaType } from "enums/options/coverageArea";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { handleUpdateCatalogAreaRequest } from "store/modules/Catalog/thunk.store";
import { useAuth } from "context/AuthContext";
import {
  isGasDoPovoCatalogProduct,
  isGasDoPovoProductInCatalog,
} from "utils/gasDoPovo";
import { getFirstApiErrorMessage } from "utils/apiError";
import { toast } from "react-toastify";
import * as Styled from "./styles";

const ZERO_PRICE_LABEL = "R$ 0,00";

function isValidProductPrice(price?: string | null): boolean {
  return Boolean(price?.trim()) && price !== ZERO_PRICE_LABEL;
}

interface ICardProductCoveredProps {
  coverageArea: CoverageAreaProps;
  accordion?: boolean;
  dealerSelected: string;
  handleOpenArea?: (value: string) => void;
  gasDoPovo?: boolean;
  workingAreaAreaType?: ECoverageAreaType;
}

const CardProductCovered = ({
  coverageArea,
  handleOpenArea,
  dealerSelected,
  accordion,
  gasDoPovo = false,
  workingAreaAreaType,
}: ICardProductCoveredProps) => {
  const { t } = useTranslation();
  const { dealerId } = useAuth();

  const products = useMemo(() => {
    const productPrices = coverageArea?.productPrices ?? [];
    const mapped = productPrices.map((x: any) => ({
      description: x?.product?.description,
      name: x?.product?.name,
      enabled: x?.enabled,
      price: formatByText?.formatCurrency(x?.price),
      productId: x?.product?.id,
    }));

    return { products: mapped };
  }, [coverageArea]);

  const { register, control, reset, watch, getValues } = useForm({
    defaultValues: products,
    mode: "onChange",
  });

  const { catalogProducts } = useAppSelector((state) => state.catalog);

  const methods = useForm();

  const { register: registerAdd, setValue } = methods;
  const addPriceField = registerAdd("productsAdd.price");

  const { fields, remove, append, replace } = useFieldArray({
    control,
    name: "products",
  });

  useEffect(() => {
    if (products?.products?.length === 0) {
      reset();
    }
    //  reset();
    replace(products.products);
  }, [coverageArea, replace, reset, products]);

  /* TODO => passar como prop */

  const [edit, setEdit] = useState(false);

  const [productSelected, setProductSelected] = useState("");
  const [productNewPrice, setProductNewPrice] = useState("");
  const [productNewId, setProductNewId] = useState("");

  const resetFormFields = () => {
    setProductSelected("");
    setProductNewId("");
    setProductNewPrice("");
  };

  const dispatch = useAppDispatch();
  const priceCannotBeZeroMessage = t("priceCannotBeZero").toString();

  const watchedProducts = watch("products");

  const IProducts = useMemo(() => {
    const currentProducts = watchedProducts ?? products.products ?? [];
    const cover = currentProducts.map((x: any) => ({
      label: formatProductNameAndDescription(x?.name, x?.description),
      id: x?.productId,
    }));

    const areaType = workingAreaAreaType;
    const hideGasDoPovoInDropdown =
      !gasDoPovo ||
      areaType === ECoverageAreaType.DealerStore ||
      areaType === ECoverageAreaType.DriverStore;

    return catalogProducts?.data
      .filter(
        (value) =>
          !hideGasDoPovoInDropdown || !isGasDoPovoCatalogProduct(value),
      )
      ?.map((x) => ({
        label: formatProductNameAndDescription(x?.name, x?.description),
        id: x?.id,
      }))
      ?.filter((x) => !cover?.find((y: any) => y.id === x.id));
  }, [
    catalogProducts?.data,
    gasDoPovo,
    products.products,
    watchedProducts,
    workingAreaAreaType,
  ]);

  const clearAddProductForm = () => {
    resetFormFields();
    setValue("productsAdd.productId", "");
    setValue("productsAdd.price", "");
    methods.reset({
      productsAdd: {
        productId: "",
        price: "",
      },
    });
  };

  const appendProductFromAddRow = () => {
    const catalogProduct = catalogProducts?.data?.find(
      (p) => p.id === productNewId,
    );
    append({
      description: catalogProduct?.description,
      name: catalogProduct?.name ?? productSelected,
      enabled: true,
      price: productNewPrice,
      productId: productNewId,
    });
    clearAddProductForm();
  };

  const handleRemoveWorkingArea = async () => {
    const productsList = getValues("products") ?? [];
    const defaultSaveErrorMessage = t(
      "failedUpdateProductPleaseAgain",
    ).toString();

    try {
      await dispatch(
        handleUpdateCatalogAreaRequest({
          data: productsList.map((x: any) => ({
            ...x,
            price: Number(
              x.price
                .replace(/[^\d,]/, "")
                .replace(",", ".")
                .replace(/[!@#$%^&*]/g, ""),
            ),
          })),
          dealerId,
          id: dealerSelected,
        }),
      ).unwrap();
      setEdit(false);
    } catch (error) {
      const apiMessage = getFirstApiErrorMessage(error);
      toast.error(apiMessage || defaultSaveErrorMessage, {
        autoClose: false,
      });
    }
  };

  useEffect(() => {
    setEdit(false);
    resetFormFields();
  }, [coverageArea?.coverageAreaId]);

  return (
    <Styled.Container margin={!accordion}>
      <form>
        {!accordion && (
          <Typography variant="caption">{t("productPriceAndStock")}</Typography>
        )}
        <Styled.CardContainer>
          {edit ? (
            <>
              {fields.map((item, index) => {
                const data = watch("products");
                const priceField = register(`products.${index}.price`);
                return (
                  <Styled.Card key={item.id}>
                    <Styled.TitleView
                      colorText={data[index]?.enabled}
                      variant="body2"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {formatProductNameAndDescription(
                        data[index]?.name,
                        data[index]?.description,
                      )}

                      {isGasDoPovoProductInCatalog(
                        data[index]?.productId,
                        catalogProducts?.data,
                      ) && (
                        <Tooltip
                          title={t("gasDoPovoDeliveryFeeTooltip")}
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: () => ({
                                bgcolor: "#000000",
                                color: "#FFFFFF",
                                maxWidth: 320,
                              }),
                            },
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{
                                color: "#000000",
                              }}
                              aria-label={String(
                                t("gasDoPovoDeliveryFeeTooltip"),
                              )}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </Styled.TitleView>

                    <Styled.InputContainer>
                      <TextField
                        {...priceField}
                        label={
                          isGasDoPovoProductInCatalog(
                            data[index]?.productId,
                            catalogProducts?.data,
                          )
                            ? t("delivery")
                            : t("price")
                        }
                        size="small"
                        onChange={(event) => {
                          onChangeMask.onChangeCurrencyMask(event);
                          priceField.onChange(event);
                        }}
                      />
                      {/* TODO => switch do component */}
                      <Switch
                        defaultChecked={watch(`products.${index}.enabled`)}
                        size="small"
                        color="success"
                        {...register(`products.${index}.enabled`)}
                      />
                      <IconButton
                        aria-label={t("delete")}
                        onClick={() => remove(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Styled.InputContainer>
                  </Styled.Card>
                );
              })}
              {!accordion && (
                <Styled.Card>
                  <Styled.InputContainer>
                    <Autocomplete
                      {...registerAdd("productsAdd.productId")}
                      style={{ width: "180px" }}
                      disablePortal
                      value={{ id: productNewId, label: productSelected }}
                      id="combo-box-demo"
                      options={IProducts || []}
                      size="small"
                      noOptionsText="Sem opções"
                      isOptionEqualToValue={(option, newValue) => {
                        return option.id === newValue.id;
                      }}
                      onChange={(_, e) => {
                        setProductSelected((e?.label as any) || "");
                        setProductNewId((e?.id as any) || "");
                      }}
                      sx={{
                        width: 180,
                        "& input": { width: 180 },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onBlur={(e) => setProductSelected(e.target.value)}
                          label={t("selectProduct")}
                        />
                      )}
                    />
                  </Styled.InputContainer>

                  <FormProvider {...methods}>
                    <Styled.InputContainer>
                      <TextField
                        {...addPriceField}
                        onChange={(event) => {
                          onChangeMask.onChangeCurrencyMask(event);
                          addPriceField.onChange(event);
                          setProductNewPrice(event.target.value);
                        }}
                        onBlur={(e) => setProductNewPrice(e.target.value)}
                        label={
                          isGasDoPovoProductInCatalog(
                            productNewId,
                            catalogProducts?.data,
                          )
                            ? t("delivery")
                            : t("price")
                        }
                        size="small"
                      />
                      {isGasDoPovoProductInCatalog(
                        productNewId,
                        catalogProducts?.data,
                      ) && (
                        <Tooltip
                          title={t("gasDoPovoDeliveryFeeTooltip")}
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: () => ({
                                bgcolor: "#000000",
                                color: "#FFFFFF",
                                maxWidth: 320,
                              }),
                            },
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{
                                color: "#000000",
                              }}
                              aria-label={String(
                                t("gasDoPovoDeliveryFeeTooltip"),
                              )}
                            />
                          </span>
                        </Tooltip>
                      )}
                      <Styled.ButttonAdd
                        disabled={false}
                        onClick={() => {
                          if (!productNewId) {
                            return;
                          }
                          if (!isValidProductPrice(productNewPrice)) {
                            toast.error(priceCannotBeZeroMessage);
                            return;
                          }
                          appendProductFromAddRow();
                        }}
                        variant="text"
                        sx={{
                          padding: 1,
                          border: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {t("add").toUpperCase()}
                        </Typography>
                      </Styled.ButttonAdd>
                    </Styled.InputContainer>
                  </FormProvider>
                </Styled.Card>
              )}
            </>
          ) : (
            <Styled.CardContainer>
              {products?.products?.map((item) => {
                return (
                  <Styled.Card key={item?.productId}>
                    <Styled.TitleView colorText={item.enabled} variant="body2">
                      {formatProductNameAndDescription(
                        item?.name,
                        item?.description,
                      )}
                      {isGasDoPovoProductInCatalog(
                        item?.productId,
                        catalogProducts?.data,
                      ) && (
                        <Tooltip
                          title={t("gasDoPovoDeliveryFeeTooltip")}
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: () => ({
                                bgcolor: "#000000",
                                color: "#FFFFFF",
                                maxWidth: 320,
                              }),
                            },
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{
                                color: "#000000",
                              }}
                              aria-label={String(
                                t("gasDoPovoDeliveryFeeTooltip"),
                              )}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </Styled.TitleView>
                    <Styled.TitleView colorText={item.enabled} variant="body2">
                      {item.price}
                    </Styled.TitleView>
                  </Styled.Card>
                );
              })}
            </Styled.CardContainer>
          )}
        </Styled.CardContainer>
        <Styled.Footer>
          {edit ? (
            <>
              <Button
                onClick={() => {
                  setEdit(false);
                }}
                color="inherit"
                variant="outlined"
                endIcon={<CloseIcon />}
              >
                {t("close")}
              </Button>
              <Button
                onClick={() => {
                  handleRemoveWorkingArea();
                }}
                color="error"
                variant="contained"
                endIcon={<SaveIcon />}
                type="button"
              >
                {t("save")}
              </Button>
            </>
          ) : (
            <Styled.DivContainerButton>
              {accordion && (
                <Button
                  onClick={() => {
                    return handleOpenArea?.(dealerSelected);
                  }}
                  color="error"
                  variant="outlined"
                  sx={{
                    border: "1px solid #000000",
                    color: "#000000",
                    ":hover": {
                      border: "1px solid #000000",
                      background: "transparent",
                    },
                  }}
                >
                  {t("Abrir área")}
                </Button>
              )}
              <Button
                onClick={() => {
                  replace(products.products);
                  reset({ products: products.products });
                  clearAddProductForm();
                  setEdit(true);
                }}
                color="error"
                variant="contained"
              >
                {t("editPrice")}
              </Button>
            </Styled.DivContainerButton>
          )}
        </Styled.Footer>
      </form>
    </Styled.Container>
  );
};

export default CardProductCovered;
